import React, { useState, useEffect, useRef } from "react";
import { 
  Plus, Search, Loader2, LayoutDashboard, Calendar, Filter, 
  ChevronDown, Layers, Zap, Play, XCircle, CheckCircle, X, Clock, AlertTriangle, ArrowLeft, Tag 
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import axios from "axios";
import { useStarterPlan } from "../../contexts/OrganizationPlanContext";

export default function TestCases({ darkMode }) {
  const navigate = useNavigate();
  const borderColor = darkMode ? "border-gray-800/50" : "border-gray-200";
  const cardBg = darkMode ? "bg-[#161922]" : "bg-white";
  const itemBg = darkMode ? "bg-[#0f111a]" : "bg-gray-50";
  const headerBg = darkMode ? "bg-[#0f111a]" : "bg-gray-50";
  
  // Fetch Project Data from Redux
  const { currentUser, selectedProject } = useSelector((state) => state.auth);
  const starterBypass = useStarterPlan();
  const projectId = selectedProject?.project_id;
  const canManageProjectTests =
    starterBypass ||
    (currentUser?.role !== "org_admin" && selectedProject?.user_role === "qa");
  const canExecuteTests = canManageProjectTests;

  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTestCaseIds, setSelectedTestCaseIds] = useState([]);
  const [bulkSprintModalOpen, setBulkSprintModalOpen] = useState(false);
  const [bulkSprintTag, setBulkSprintTag] = useState("");
  const [isBulkSprintSaving, setIsBulkSprintSaving] = useState(false);
  
  // Test Suite State
  const [selectedSuite, setSelectedSuite] = useState(null);

  // Filter and Sort States
  const [searchQuery, setSearchQuery] = useState("");
  const [dateSort, setDateSort] = useState("newest");
  const [statusFilter, setStatusFilter] = useState("All");
  const [scenarioFilter, setScenarioFilter] = useState("All"); // 👉 NEW: Scenario Filter State
  
  // Dropdown UI State
  const [openDropdown, setOpenDropdown] = useState(null); 

  // ==========================================
  // BATCH ORCHESTRATION STATES
  // ==========================================
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [batchQueue, setBatchQueue] = useState([]);
  const [batchProgress, setBatchProgress] = useState(0);
  const [batchResults, setBatchResults] = useState([]);
  const [isBatchRunning, setIsBatchRunning] = useState(false);
  const [currentlyRunning, setCurrentlyRunning] = useState(null);
  const [batchStartTime, setBatchStartTime] = useState(null);
  const [eta, setEta] = useState("Calculating...");
  
  const cancelBatchRef = useRef(false); 

  const [showBatchFailurePopup, setShowBatchFailurePopup] = useState(false);
  const [failedBatchRuns, setFailedBatchRuns] = useState([]);

  // Fetch Test Cases
  useEffect(() => {
    const fetchTestCases = async () => {
      setSelectedTestCaseIds([]);
      if (!projectId) {
        setTestCases([]);
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(
          `https://api.testoraglobal.in/api/testcases/project/${projectId}`,
        );
        if (response.ok) {
          const data = await response.json();
          setTestCases(data);
        } else {
          console.error("Failed to fetch test cases");
        }
      } catch (error) {
        console.error("Error fetching test cases:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestCases();
  }, [projectId]);

  const handleCreate = () => navigate("/test-cases/create");
  const handleView = (tc) => navigate(`/test-cases/${tc.internal_id || tc.test_id}`);
  const toggleTestCaseSelection = (testCaseId) => {
    setSelectedTestCaseIds((current) =>
      current.includes(testCaseId)
        ? current.filter((id) => id !== testCaseId)
        : [...current, testCaseId]
    );
  };

  const handleBulkSprintAssign = async () => {
    const sprintTag = bulkSprintTag.trim();
    if (!sprintTag) {
      toast.error("Enter a sprint or release name.");
      return;
    }

    setIsBulkSprintSaving(true);
    try {
      await axios.put("https://api.testoraglobal.in/api/testcases/bulk-sprint", {
        test_case_ids: selectedTestCaseIds,
        sprint_tag: sprintTag,
      });
      setTestCases((current) =>
        current.map((testCase) =>
          selectedTestCaseIds.includes(testCase.internal_id || testCase.test_id)
            ? { ...testCase, sprint_tag: sprintTag }
            : testCase
        )
      );
      setSelectedTestCaseIds([]);
      setBulkSprintModalOpen(false);
      setBulkSprintTag("");
      toast.success("Sprint assigned to selected test cases.");
    } catch (error) {
      toast.error(
        error.response?.data?.error || error.message || "Failed to assign sprint."
      );
    } finally {
      setIsBulkSprintSaving(false);
    }
  };

  // ==========================================
  // SUITE COMPUTATION LOGIC
  // ==========================================
  const suitesList = Array.from(new Set(testCases.map((tc) => tc.module || "Unassigned").filter(Boolean)));
  const suiteData = suitesList.map(suite => {
    const tcs = testCases.filter(tc => (tc.module || "Unassigned") === suite);
    return {
      name: suite,
      total: tcs.length,
      passed: tcs.filter(tc => tc.status === "Pass").length,
      failed: tcs.filter(tc => tc.status === "Fail").length,
      active: tcs.filter(tc => tc.status === "Active" || tc.status === "Active(Not Executed)").length
    };
  });

  // 👉 NEW: Filter test cases based on selected suite, status, and scenario
  const filteredAndSortedTestCases = testCases
    .filter((tc) => {
      // Must match selected suite if one is selected
      if (selectedSuite && (tc.module || "Unassigned") !== selectedSuite) return false;

      // 1. Search Query Logic
      const matchesSearch = 
        tc.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tc.test_id?.toLowerCase().includes(searchQuery.toLowerCase());
      
      // 2. Status Logic
      const tcStatus = tc.status === "Active(Not Executed)" ? "Active" : tc.status || "Active";
      const matchesStatus = statusFilter === "All" || tcStatus === statusFilter;
      
      // 3. Scenario Logic (Checks both 'type' and 'category' for broad coverage)
      const matchesScenario = scenarioFilter === "All" || 
        (tc.type?.toLowerCase().includes(scenarioFilter.toLowerCase()) || 
         tc.category?.toLowerCase().includes(scenarioFilter.toLowerCase()));

      return matchesSearch && matchesStatus && matchesScenario;
    })
    .sort((a, b) => {
      const dateA = new Date(a.created_date).getTime();
      const dateB = new Date(b.created_date).getTime();
      return dateSort === "newest" ? dateB - dateA : dateA - dateB;
    });

  // ==========================================
  // BATCH LOGIC
  // ==========================================
  const triggerBatch = (type) => {
    const contextCases = selectedSuite 
      ? testCases.filter(tc => (tc.module || "Unassigned") === selectedSuite) 
      : testCases;

    let queue = [];
    if (type === "All") queue = [...contextCases];
    else if (type === "Passed") queue = contextCases.filter(t => t.status === "Pass");
    else if (type === "Failed") queue = contextCases.filter(t => t.status === "Fail");
    else if (type === "Positive") queue = contextCases.filter(t => t.type?.toLowerCase().includes("positive"));
    else if (type === "Negative") queue = contextCases.filter(t => t.type?.toLowerCase().includes("negative"));
    else if (type === "Edge") queue = contextCases.filter(t => t.type?.toLowerCase().includes("edge"));

    if (queue.length === 0) {
      toast.error(`No ${type} test cases found in this context.`);
      return;
    }

    setBatchQueue(queue);
    setBatchProgress(0);
    setBatchResults([]);
    setFailedBatchRuns([]);
    setBatchModalOpen(true);
    setOpenDropdown(null);
  };

  const startBatchExecution = async () => {
    setIsBatchRunning(true);
    setBatchStartTime(Date.now());
    cancelBatchRef.current = false;
    let completedCount = 0;
    const currentResults = [];
    const localFailedRuns = []; 

    for (const tc of batchQueue) {
      if (cancelBatchRef.current) {
        toast("Batch execution stopped by user.", { icon: "🛑" });
        break;
      }

      setCurrentlyRunning(tc);
      const stepStartTime = Date.now();
      let finalStatus = "Fail";
      let formattedActualResult = "";
      let screenshotsArray = [];

      try {
        const payload = {
          test_id: tc.internal_id || tc.test_id,
          test_case: {
            "test-case-title": tc.title,
            test_steps: tc.test_steps || [],
            expected_result: tc.expected_result || "Execute steps and verify success.",
            base_url: tc.base_url,
          },
          project_id: projectId,
          headless: true
        };

        const { data } = await axios.post(
          "https://api.testoraglobal.in/api/run-autonomous-test",
          payload,
        );
        
        if (data.screenshots && Array.isArray(data.screenshots)) {
          screenshotsArray = data.screenshots.map((s) => 
            s.startsWith("http") ? s : `https://api.testoraglobal.in${s}`
          );
        }

        if (data.success) {
          finalStatus = data.status;
          if (data.status === "Pass") {
            formattedActualResult = `✅ Successfully verified: ${tc.expected_result}`;
          } else {
            const cleanErrorMessage = data.message || "Execution encountered an error.";
            formattedActualResult = `❌ Failed to verify: ${tc.expected_result}\n\nError Summary: ${cleanErrorMessage}`;
            
            localFailedRuns.push({
              title: `[Batch Fail] ${tc.title}`,
              description: `Batch execution failed for Test Case: ${tc.test_id || "TC"}. Please review the actual vs expected results.`,
              expected_result: tc.expected_result || "",
              actual_result: formattedActualResult || "",
              module: tc.module || "",
              adjecent_testcaseid: tc.internal_id || tc.test_id || "",
              steps: tc.test_steps || [],
              screenshots: screenshotsArray.slice(-2) 
            });
          }
        } else {
          formattedActualResult = `❌ Execution failed: ${data.message}`;
          localFailedRuns.push({
            title: `[Batch Error] ${tc.title}`,
            description: `Execution error for Test Case: ${tc.test_id || "TC"}.`,
            expected_result: tc.expected_result || "",
            actual_result: formattedActualResult,
            module: tc.module || "",
            adjecent_testcaseid: tc.internal_id || tc.test_id || "",
            steps: tc.test_steps || [],
            screenshots: screenshotsArray.slice(-2)
          });
        }

        await axios.put(
          `https://api.testoraglobal.in/api/testcases/${tc.internal_id || tc.test_id}`,
          {
            status: finalStatus,
            actual_result: formattedActualResult,
          },
        );

      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          cancelBatchRef.current = true;
          break;
        }

        finalStatus = "Error";
        const executionError = err.response?.data?.message || "Failed to run autonomous test.";
        formattedActualResult = `❌ Connection Error: ${executionError}`;
        localFailedRuns.push({
            title: `[Batch Error] ${tc.title}`,
            description: `Failed to connect to execution engine.`,
            expected_result: tc.expected_result || "",
            actual_result: formattedActualResult,
            module: tc.module || "",
            adjecent_testcaseid: tc.internal_id || tc.test_id || "",
            steps: tc.test_steps || [],
            screenshots: []
        });
      }

      const duration = ((Date.now() - stepStartTime) / 1000).toFixed(1) + "s";

      currentResults.push({
        id: tc.internal_id || tc.test_id,
        title: tc.title,
        status: finalStatus,
        duration: duration,
      });

      setBatchResults([...currentResults]);

      setTestCases(prev => prev.map(t => 
        (t.internal_id === tc.internal_id || t.test_id === tc.test_id) 
          ? { ...t, status: finalStatus, actual_result: formattedActualResult } 
          : t
      ));

      completedCount++;
      setBatchProgress(completedCount);
    }

    setIsBatchRunning(false);
    setCurrentlyRunning(null);
    
    if (localFailedRuns.length > 0) {
        setFailedBatchRuns(localFailedRuns);
        setShowBatchFailurePopup(true);
    }
  };

  const cancelBatch = () => {
    cancelBatchRef.current = true;
    if (!isBatchRunning) setBatchModalOpen(false);
  };

  useEffect(() => {
    let interval;
    if (isBatchRunning && batchProgress > 0 && batchStartTime) {
      interval = setInterval(() => {
        const elapsed = Date.now() - batchStartTime;
        const avgTimePerTest = elapsed / batchProgress;
        const remainingTests = batchQueue.length - batchProgress;
        const estimatedRemainingMs = avgTimePerTest * remainingTests;
        
        if (estimatedRemainingMs < 0) {
          setEta("Almost done...");
        } else {
          const mins = Math.floor(estimatedRemainingMs / 60000);
          const secs = Math.floor((estimatedRemainingMs % 60000) / 1000);
          setEta(`${mins}m ${secs}s`);
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isBatchRunning, batchProgress, batchStartTime, batchQueue.length]);

  return (
    <div className="px-8 pt-8 h-[calc(100vh-4rem)] max-w-7xl mx-auto animate-in fade-in duration-500 flex flex-col">
      
      {/* --- FIXED HEADER & CONTROLS --- */}
      <div className="shrink-0">
        <div className="flex justify-between items-center mb-8">
          <div>
            <div className="flex items-center gap-3">
              {selectedSuite && (
                <button 
                  onClick={() => {
                    setSelectedSuite(null);
                    setSearchQuery("");
                    setStatusFilter("All");
                    setScenarioFilter("All"); // Reset the new scenario filter
                  }} 
                  className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                  title="Back to Suites"
                >
                  <ArrowLeft className="text-gray-600 dark:text-gray-300" size={20} />
                </button>
              )}
              <h2 className="text-3xl font-bold tracking-tight">
                {selectedSuite ? `Test Cases - ${selectedSuite}` : "Test Suites"}
              </h2>
            </div>
            <p className="text-gray-500 text-sm mt-1">
              {selectedSuite 
                ? `Manage and execute tests for the ${selectedSuite} suite` 
                : "Select a module suite to view and manage its associated test cases"}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className={`px-4 py-2 rounded-xl border ${borderColor} ${cardBg} flex items-center gap-3 shadow-sm`}>
              <LayoutDashboard size={16} className="text-purple-500" />
              <div className="flex flex-col">
                <span className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Active Project</span>
                <span className="text-sm font-semibold">{selectedProject?.project_name || "None Selected"}</span>
              </div>
            </div>

            {canExecuteTests && (
            <div className="relative z-30">
              <button
                onClick={() => setOpenDropdown(openDropdown === 'batch' ? null : 'batch')}
                className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-xl flex items-center gap-2 font-bold transition-all shadow-lg shadow-indigo-500/20"
              >
                <Zap size={18} fill="currentColor" /> Automate Batch <ChevronDown size={14} />
              </button>
              
              {openDropdown === 'batch' && (
                <div className={`absolute top-full right-0 mt-2 w-56 overflow-hidden rounded-xl border ${borderColor} ${cardBg} shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200 z-50`}>
                  <div className="flex flex-col py-1">
                    <div className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-800/50 mb-1">
                      {selectedSuite ? "Run Current Suite" : "Run All Suites"}
                    </div>
                    {[
                      { label: "Run All Test Cases", type: "All" },
                      { label: "Run Failed Tests", type: "Failed" },
                      { label: "Run Passed Tests", type: "Passed" },
                      { label: "Run Positive Tests", type: "Positive" },
                      { label: "Run Negative Tests", type: "Negative" },
                      { label: "Run Edge Cases", type: "Edge" },
                    ].map(opt => (
                      <div
                        key={opt.type}
                        onClick={() => triggerBatch(opt.type)}
                        className="px-4 py-2.5 text-sm cursor-pointer transition-colors text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-medium flex items-center gap-2"
                      >
                        <Play size={14} /> {opt.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            )}

            {canManageProjectTests && (
              <button
                onClick={handleCreate}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-xl flex items-center gap-2 font-bold transition-all shadow-lg shadow-purple-500/20"
              >
                <Plus size={18} /> New Test Case
              </button>
            )}
          </div>
        </div>

        {/* --- CONTROL PANEL (ONLY VISIBLE INSIDE A SUITE) --- */}
        {selectedSuite && (
          <div className={`p-4 rounded-2xl border ${cardBg} ${borderColor} mb-6 flex gap-4 items-center relative z-20 animate-in fade-in flex-wrap`}>
            {openDropdown && (
              <div className="fixed inset-0 z-10" onClick={() => setOpenDropdown(null)} />
            )}

            <div className={`flex-1 min-w-[250px] flex items-center px-4 py-2 rounded-xl ${itemBg} border ${borderColor} relative z-20`}>
              <Search size={18} className="text-gray-500 mr-2 shrink-0" />
              <input
                type="text"
                placeholder="Search by ID or Title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent border-none outline-none text-sm w-full text-gray-700 dark:text-gray-200"
              />
            </div>

            {canManageProjectTests && selectedTestCaseIds.length > 0 && (
              <button
                type="button"
                onClick={() => setBulkSprintModalOpen(true)}
                className="rounded-xl bg-purple-600 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-purple-700"
              >
                Bulk Assign Sprint ({selectedTestCaseIds.length})
              </button>
            )}

            {/* 👉 NEW: SCENARIO FILTER DROPDOWN */}
            <div className="relative z-20">
              <div
                onClick={() => setOpenDropdown(openDropdown === 'scenario' ? null : 'scenario')}
                className={`flex items-center px-4 py-2 rounded-xl ${itemBg} border ${borderColor} cursor-pointer hover:border-purple-500/50 transition-colors select-none`}
              >
                <Tag size={16} className="text-purple-500 mr-2 shrink-0" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200 mr-3 whitespace-nowrap">
                  {scenarioFilter === "All" ? "All Scenarios" : scenarioFilter}
                </span>
                <ChevronDown size={14} className="text-gray-500 shrink-0" />
              </div>

              {openDropdown === 'scenario' && (
                <div 
                  className={`absolute top-full right-0 mt-2 w-48 overflow-y-auto custom-scrollbar rounded-xl border ${borderColor} ${cardBg} shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200 z-50`}
                  style={{ maxHeight: "280px" }}
                >
                  <div className="flex flex-col py-1">
                    {["All", "Functional", "UI/UX", "E2E", "Positive", "Negative", "Performance", "Integration"].map(scenario => (
                      <div
                        key={scenario}
                        onClick={() => { setScenarioFilter(scenario); setOpenDropdown(null); }}
                        className={`px-4 py-3 text-sm cursor-pointer transition-colors ${scenarioFilter === scenario ? 'text-purple-500 bg-purple-500/10 font-bold' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                      >
                        {scenario === "All" ? "All Scenarios" : scenario}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="relative z-20">
              <div
                onClick={() => setOpenDropdown(openDropdown === 'status' ? null : 'status')}
                className={`flex items-center px-4 py-2 rounded-xl ${itemBg} border ${borderColor} cursor-pointer hover:border-purple-500/50 transition-colors select-none`}
              >
                <Filter size={16} className="text-purple-500 mr-2 shrink-0" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200 mr-3 whitespace-nowrap">
                  {statusFilter === "All" ? "All Statuses" : statusFilter === "Active" ? "Active" : statusFilter}
                </span>
                <ChevronDown size={14} className="text-gray-500 shrink-0" />
              </div>

              {openDropdown === 'status' && (
                <div 
                  className={`absolute top-full right-0 mt-2 w-48 overflow-y-auto custom-scrollbar rounded-xl border ${borderColor} ${cardBg} shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200 z-50`}
                  style={{ maxHeight: "220px" }}
                >
                  <div className="flex flex-col py-1">
                    {["All", "Active", "Pass", "Fail"].map(status => (
                      <div
                        key={status}
                        onClick={() => { setStatusFilter(status); setOpenDropdown(null); }}
                        className={`px-4 py-3 text-sm cursor-pointer transition-colors ${statusFilter === status ? 'text-purple-500 bg-purple-500/10 font-bold' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                      >
                        {status === "All" ? "All Statuses" : status === "Active" ? "Active (Needs Run)" : status}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="relative z-20">
              <div
                onClick={() => setOpenDropdown(openDropdown === 'date' ? null : 'date')}
                className={`flex items-center px-4 py-2 rounded-xl ${itemBg} border ${borderColor} cursor-pointer hover:border-purple-500/50 transition-colors select-none`}
              >
                <Calendar size={16} className="text-purple-500 mr-2 shrink-0" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200 mr-3 whitespace-nowrap">
                  {dateSort === "newest" ? "Newest First" : "Oldest First"}
                </span>
                <ChevronDown size={14} className="text-gray-500 shrink-0" />
              </div>

              {openDropdown === 'date' && (
                <div 
                  className={`absolute top-full right-0 mt-2 w-48 overflow-y-auto custom-scrollbar rounded-xl border ${borderColor} ${cardBg} shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200 z-50`}
                  style={{ maxHeight: "220px" }}
                >
                  <div className="flex flex-col py-1">
                    <div
                      onClick={() => { setDateSort("newest"); setOpenDropdown(null); }}
                      className={`px-4 py-3 text-sm cursor-pointer transition-colors ${dateSort === 'newest' ? 'text-purple-500 bg-purple-500/10 font-bold' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                    >
                      Newest First (Top)
                    </div>
                    <div
                      onClick={() => { setDateSort("oldest"); setOpenDropdown(null); }}
                      className={`px-4 py-3 text-sm cursor-pointer transition-colors ${dateSort === 'oldest' ? 'text-purple-500 bg-purple-500/10 font-bold' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                    >
                      Oldest First (Bottom)
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* --- SCROLLABLE BODY SECTION --- */}
      <div className="flex-1 overflow-y-auto custom-scrollbar pb-12 pr-2 relative z-0">
        {!projectId ? (
          <div className={`flex flex-col items-center justify-center p-12 rounded-3xl border ${cardBg} ${borderColor} text-center h-full`}>
            <LayoutDashboard size={48} className="text-gray-400 mb-4" />
            <h3 className="text-xl font-bold mb-2">No Project Selected</h3>
            <p className="text-gray-500 max-w-md">
              Please select an active project from your dashboard to view or manage its test cases.
            </p>
          </div>
        ) : loading ? (
          <div className="flex flex-col items-center justify-center h-full text-purple-500">
            <Loader2 size={40} className="animate-spin mb-4" />
            <p className="text-gray-500 font-medium">Loading test cases...</p>
          </div>
        ) : !selectedSuite ? (
          // 👉 RENDER SUITES GRID
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in slide-in-from-bottom-4 duration-500">
            {suiteData.length > 0 ? (
              suiteData.map(suite => (
                <div 
                  key={suite.name} 
                  onClick={() => setSelectedSuite(suite.name)}
                  className={`p-6 rounded-3xl border ${cardBg} ${borderColor} hover:border-purple-500/50 cursor-pointer transition-all hover:shadow-xl group`}
                >
                   <div className="flex justify-between items-start mb-4">
                     <div className="p-3 bg-purple-500/10 rounded-xl">
                       <Layers className="text-purple-500" size={24} />
                     </div>
                     <span className="text-xs font-bold px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                       {suite.total} Tests
                     </span>
                   </div>
                   <h3 className="text-xl font-black tracking-wide mb-2 group-hover:text-purple-500 transition-colors uppercase">
                     {suite.name}
                   </h3>
                   <div className="flex gap-4 text-sm mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
                     <div className="flex items-center gap-1.5 text-green-500 font-bold" title="Passed">
                       <CheckCircle size={14} /> {suite.passed}
                     </div>
                     <div className="flex items-center gap-1.5 text-red-500 font-bold" title="Failed">
                       <XCircle size={14} /> {suite.failed}
                     </div>
                     <div className="flex items-center gap-1.5 text-blue-500 font-bold" title="Active/Unexecuted">
                       <Clock size={14} /> {suite.active}
                     </div>
                   </div>
                </div>
              ))
            ) : (
              <div className="col-span-full p-12 text-center text-gray-500">
                No test suites found. Create your first test case to begin.
              </div>
            )}
          </div>
        ) : (
          // 👉 RENDER INDIVIDUAL SUITE TEST CASES LIST
          <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
            {filteredAndSortedTestCases.length > 0 ? (
              filteredAndSortedTestCases.map((tc) => (
                <div
                  key={tc.internal_id || tc.test_id}
                  onClick={() => handleView(tc)}
                  className={`p-6 rounded-3xl border transition-all group cursor-pointer active:scale-[0.99] ${
                    tc.status === "Pass"
                      ? `border-green-500/30 hover:border-green-500/50 ${darkMode ? "bg-green-500/5" : "bg-green-50"}`
                      : tc.status === "Fail"
                        ? `border-red-500/30 hover:border-red-500/50 ${darkMode ? "bg-red-500/5" : "bg-red-50"}`
                        : `${cardBg}${borderColor} hover:border-purple-500/30`
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex min-w-0 items-start gap-3">
                      {canManageProjectTests && (
                        <input
                          type="checkbox"
                          aria-label={`Select ${tc.test_id || tc.title}`}
                          checked={selectedTestCaseIds.includes(tc.internal_id || tc.test_id)}
                          onClick={(event) => event.stopPropagation()}
                          onChange={() => toggleTestCaseSelection(tc.internal_id || tc.test_id)}
                          className="mt-1 h-4 w-4 shrink-0 accent-purple-600"
                        />
                      )}
                      <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-3">
                        <span className="text-indigo-400 font-bold text-xs uppercase tracking-widest">
                          {tc.test_id}
                        </span>
                        {tc.jira_key && tc.jira_url && (
                          <a href={tc.jira_url} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()} className="text-blue-500 text-xs font-semibold hover:underline">
                            Jira Story: {tc.jira_key}
                          </a>
                        )}

                        <span
                          className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${
                            tc.type?.toLowerCase().includes("negative")
                              ? "bg-red-500/10 text-red-500"
                              : tc.type?.toLowerCase().includes("positive")
                                ? "bg-green-500/10 text-green-500"
                                : "bg-orange-500/10 text-orange-500"
                          }`}
                        >
                          {tc.type || "Edge Case"}
                        </span>
                      </div>

                      <h4 className="text-lg font-bold group-hover:text-purple-400 transition-colors">
                        {tc.title}
                      </h4>

                      <p className="text-xs text-gray-500">
                        Feature: {tc["feature/submodule"] || tc.feature || "N/A"} •{" "}
                        Category: {tc.category} •{" "}
                        <span className="text-gray-400">
                          Created: {new Date(tc.created_date).toLocaleDateString()}
                        </span>
                      </p>
                      </div>
                    </div>

                    <span
                      className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase border ${
                        tc.status === "Pass"
                          ? "bg-green-500/10 text-green-600 dark:text-green-500 border-green-500/20"
                          : tc.status === "Fail"
                            ? "bg-red-500/10 text-red-600 dark:text-red-500 border-red-500/20"
                            : tc.status === "Active" || tc.status === "Active(Not Executed)"
                              ? "bg-blue-500/10 text-blue-600 dark:text-blue-500 border-blue-500/20"
                              : "bg-gray-500/10 text-gray-500 border-gray-500/20"
                      }`}
                    >
                      {tc.status === "Active(Not Executed)" ? "Active" : tc.status || "Draft"}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className={`p-12 rounded-3xl border ${cardBg} ${borderColor} text-center mt-10`}>
                <p className="text-gray-500 mb-4">
                  No test cases match your current filters in the <strong>{selectedSuite}</strong> suite.
                </p>
                {canManageProjectTests && (
                  <button
                    onClick={handleCreate}
                    className="text-purple-500 font-bold hover:underline"
                  >
                    Create a new test case
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {bulkSprintModalOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4">
          <div className={`w-full max-w-md rounded-2xl border p-6 shadow-2xl ${cardBg} ${borderColor}`}>
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold">Bulk Assign Sprint</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Assign a sprint or release to {selectedTestCaseIds.length} selected test cases.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setBulkSprintModalOpen(false)}
                disabled={isBulkSprintSaving}
                aria-label="Close bulk sprint assignment"
                className="rounded-lg p-1 text-gray-500 hover:bg-gray-500/10 disabled:opacity-50"
              >
                <X size={18} />
              </button>
            </div>
            <label className="mb-5 block">
              <span className="mb-1 block text-sm font-medium">Sprint / Release</span>
              <input
                type="text"
                value={bulkSprintTag}
                onChange={(event) => setBulkSprintTag(event.target.value)}
                placeholder="e.g. Sprint 12 or Release 2.4"
                className={`w-full rounded-lg border ${borderColor} ${itemBg} px-3 py-2 outline-none focus:border-purple-500`}
                autoFocus
              />
            </label>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setBulkSprintModalOpen(false)}
                disabled={isBulkSprintSaving}
                className={`rounded-lg border ${borderColor} px-4 py-2 text-sm font-semibold disabled:opacity-50`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkSprintAssign}
                disabled={isBulkSprintSaving || !bulkSprintTag.trim()}
                className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isBulkSprintSaving ? "Saving..." : "Assign Sprint"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================== */}
      {/* BATCH EXECUTION MODAL */}
      {/* ========================================== */}
      {batchModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in">
          <div className={`w-full max-w-2xl rounded-2xl shadow-2xl border flex flex-col overflow-hidden ${cardBg} ${borderColor}`}>
            
            <div className={`px-6 py-4 border-b flex items-center justify-between ${headerBg} ${borderColor}`}>
              <h3 className="font-bold text-lg text-white flex items-center gap-2">
                <Zap size={20} className="text-indigo-500" />
                Batch Suite Execution {selectedSuite && `- ${selectedSuite}`}
              </h3>
              <button
                onClick={cancelBatch}
                className="p-2 hover:bg-gray-500/20 rounded-full transition-colors text-gray-400"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {!isBatchRunning && batchProgress === 0 && (
                <div className="space-y-6">
                  <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-xl p-4">
                    <p className="text-indigo-400 font-bold mb-1">Execution Summary</p>
                    <p className="text-sm text-gray-300">You are about to automate <strong className="text-white text-lg">{batchQueue.length}</strong> test cases sequentially. The UI will update in real-time as each test completes.</p>
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <button onClick={cancelBatch} className="px-5 py-2.5 rounded-xl font-bold text-sm text-gray-400 hover:text-white">Cancel</button>
                    <button onClick={startBatchExecution} className="bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 shadow-lg shadow-indigo-500/20">
                      <Play size={16} fill="currentColor" /> Start Automation
                    </button>
                  </div>
                </div>
              )}

              {(isBatchRunning || batchProgress > 0) && (
                <div className="space-y-6">
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mb-1">
                        {isBatchRunning ? "Executing Batch..." : "Batch Complete"}
                      </p>
                      <h4 className="text-2xl font-black text-white">
                        {batchProgress} / {batchQueue.length} <span className="text-lg font-medium text-gray-500">Completed</span>
                      </h4>
                    </div>
                    {isBatchRunning && (
                      <div className="text-right">
                        <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mb-1">Estimated Time Left</p>
                        <p className="text-lg font-bold text-indigo-400 flex items-center justify-end gap-1.5"><Clock size={16} /> {eta}</p>
                      </div>
                    )}
                  </div>

                  <div className="h-3 w-full bg-gray-900 rounded-full overflow-hidden border border-gray-800">
                    <div 
                      className="h-full bg-indigo-500 transition-all duration-500 relative"
                      style={{ width: `${(batchProgress / batchQueue.length) * 100}%` }}
                    >
                      {isBatchRunning && <div className="absolute inset-0 bg-white/20 animate-pulse"></div>}
                    </div>
                  </div>

                  {isBatchRunning && currentlyRunning && (
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex items-center gap-4">
                      <Loader2 size={24} className="text-blue-500 animate-spin shrink-0" />
                      <div className="overflow-hidden">
                        <p className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-0.5">Currently Running</p>
                        <p className="text-sm font-semibold text-white truncate">{currentlyRunning.title}</p>
                      </div>
                    </div>
                  )}

                  <div className="border border-gray-800 rounded-xl bg-[#0a0c10] overflow-hidden flex flex-col">
                    <div className="px-4 py-2 border-b border-gray-800 bg-gray-900/50 text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Execution Log
                    </div>
                    <div className="h-64 overflow-y-auto custom-scrollbar p-2 space-y-2">
                      {batchResults.length === 0 && !isBatchRunning ? (
                        <p className="text-center text-gray-600 text-sm mt-8">Waiting to start...</p>
                      ) : (
                        batchResults.map((res, i) => (
                          <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-gray-800/50 bg-gray-900/30 animate-in slide-in-from-bottom-2">
                            <div className="flex items-center gap-3 overflow-hidden">
                              {res.status === "Pass" ? <CheckCircle size={18} className="text-green-500 shrink-0" /> : <XCircle size={18} className="text-red-500 shrink-0" />}
                              <span className="text-sm text-gray-300 font-medium truncate">{res.title}</span>
                            </div>
                            <span className="text-xs font-mono text-gray-500 shrink-0 ml-4">{res.duration}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end pt-2">
                    {isBatchRunning ? (
                      <button onClick={cancelBatch} className="bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white border border-red-500/20 px-6 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2">
                        <X size={16} /> Stop Execution
                      </button>
                    ) : (
                      <button onClick={() => setBatchModalOpen(false)} className="bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-2.5 rounded-xl font-bold text-sm transition-all">
                        Close
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ========================================== */}
      {/* NEW: BATCH FAILURE INTELLIGENCE POPUP */}
      {/* ========================================== */}
      {showBatchFailurePopup && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in">
          <div className={`w-full max-w-md rounded-2xl shadow-2xl border p-6 ${cardBg} ${borderColor} text-center`}>
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-500/20">
              <AlertTriangle size={32} className="text-red-500" />
            </div>
            <h3 className={`text-xl font-bold mb-2 text-white`}>
              Batch Complete with {failedBatchRuns.length} Failures
            </h3>
            <p className={`text-sm text-gray-400 mb-6 leading-relaxed`}>
              {failedBatchRuns.length} test cases failed during this batch execution. Do you want to seamlessly report them as bugs with pre-filled details and screenshots?
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowBatchFailurePopup(false)}
                className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all border ${borderColor} text-gray-400 hover:text-white`}
              >
                No, Skip
              </button>
              {canManageProjectTests && (
                <button
                  onClick={() => {
                    setShowBatchFailurePopup(false);
                    setBatchModalOpen(false);
                    navigate("/bug-optimizer/createbug", { state: { prefillDataArray: failedBatchRuns } });
                  }}
                  className="bg-red-600 hover:bg-red-500 text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-red-500/20 transition-all flex items-center gap-2"
                >
                  Report {failedBatchRuns.length} Bugs
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}